import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, X } from "lucide-react";

const SESSION_KEY = "cowrieking_give_prompt_seen";

const MESSAGES = [
  { match: /^\/journal\//, text: "Moved by this essay? Stories like this become food, school fees, and dignity through Eyes of Hope." },
  { match: /^\/gallery/, text: "Every photograph here funds the family in it. Your gift keeps that promise alive." },
  { match: /^\/heritage|^\/museum/, text: "Help preserve Ada's living heritage for the generations still coming." },
  { match: /^\/voices/, text: "These voices survive because people like you choose to keep them heard." },
  { match: /^\/philosophy|^\/about/, text: "If this work speaks to you, a small gift carries it further than words." },
];

export default function ContextualGivePrompt() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
  });

  const msg = MESSAGES.find((m) => m.match.test(pathname));

  useEffect(() => {
    setVisible(false);
    if (dismissed || !msg) return;
    const timer = setTimeout(() => setVisible(true), 18000);
    const onScroll = () => {
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth > 0.6) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname, dismissed, msg]);

  const dismiss = () => {
    setDismissed(true);
    setVisible(false);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
  };

  if (!msg || !visible || dismissed) return null;

  return (
    <aside
      data-testid="contextual-give-prompt"
      role="complementary"
      aria-label="Support Eyes of Hope Foundation"
      className="fixed bottom-5 right-5 z-40 max-w-sm bg-[#141210] border border-[#d4af37]/40 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.8)] p-5 animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <button
        onClick={dismiss}
        data-testid="give-prompt-dismiss"
        aria-label="Dismiss donation prompt"
        className="absolute top-3 right-3 text-[#78716c] hover:text-[#f4f0ec] transition-colors p-1"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-4">
        <Heart size={18} className="text-[#d4af37] flex-shrink-0 mt-1" aria-hidden="true" />
        <div>
          <p className="text-sm text-[#f4f0ec] leading-relaxed">{msg.text}</p>
          <div className="mt-4 flex items-center gap-4">
            <Link
              to="/eyes-of-hope#donate"
              onClick={dismiss}
              data-testid="give-prompt-donate"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4af37] hover:bg-[#c39f2f] text-[#0a0908] text-[11px] font-bold uppercase tracking-[0.2em] transition-colors"
            >
              Give Hope
            </Link>
            <button
              onClick={dismiss}
              data-testid="give-prompt-later"
              className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#78716c] hover:text-[#a8a29e] transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
